// Match chat. One box per match, showing only what was said in the one you are
// in — the server addresses every message to that match's room, so a game is
// never talked into by people who are not in it.
//
// The keyboard belongs to the game everywhere else on this page, which is
// exactly why this is an <input>: the game's key handling ignores events whose
// target is a text field, so typing "1" here cannot also recall a control group.
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

// The sides' colours, matching the sprites the units are drawn with so a name
// in the log reads as the same side you can see on the map.
const SIDE_COLOURS = { 0: "#7fb2ff", 1: "#ff8f7a" };
const NEUTRAL = "#d8c79c";

const panel = {
  marginTop: "12px",
  width: "800px",
  boxSizing: "border-box",
  color: "#f0e2c0",
  background: "linear-gradient(#3b3120, #2a2214)",
  border: "3px solid #6c5836",
  borderRadius: "8px",
  fontFamily: "Georgia, 'Times New Roman', serif",
  overflow: "hidden",
};

const log = {
  height: "150px",
  overflowY: "auto",
  padding: "10px 14px",
  fontSize: "15px",
  lineHeight: 1.45,
};

const form = {
  display: "flex",
  gap: "8px",
  padding: "10px 12px",
  borderTop: "2px solid #6c5836",
  background: "rgba(24,20,12,0.45)",
};

const field = {
  flex: 1,
  padding: "9px 11px",
  fontSize: "15px",
  fontFamily: "inherit",
  color: "#f3e6c8",
  background: "rgba(24,20,12,0.72)",
  border: "2px solid #6c5836",
  borderRadius: "6px",
  outline: "none",
};

const sendButton = (enabled) => ({
  cursor: enabled ? "pointer" : "default",
  opacity: enabled ? 1 : 0.45,
  padding: "0 18px",
  fontSize: "15px",
  fontWeight: 700,
  letterSpacing: "1px",
  fontFamily: "inherit",
  color: "#2a1c08",
  background: "linear-gradient(#f5d98a, #d8a441 55%, #b9822b)",
  border: "2px solid #7a5518",
  borderRadius: "6px",
});

const empty = { opacity: 0.55, fontStyle: "italic" };
const stamp = { opacity: 0.45, fontSize: "12px", marginRight: "6px" };

// Server time, shown in the reader's own zone. Anything that won't parse simply
// gets no timestamp rather than an "Invalid Date" in the middle of the log.
const timeOf = (at) => {
  const when = new Date(at);
  return Number.isNaN(when.getTime())
    ? ""
    : when.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

// How close to the bottom still counts as "following along". Anything further
// up and the reader has deliberately scrolled back.
const FOLLOW_SLACK_PX = 40;

const ChatBox = ({ messages, onSend, mySide }) => {
  const [draft, setDraft] = useState("");
  const logRef = useRef(null);
  // Whether the log was pinned to the bottom *before* this message arrived.
  // Read in a layout effect, so it has to be recorded on the way in rather than
  // measured after the new line has already changed the scroll height.
  const wasFollowing = useRef(true);

  useEffect(() => {
    const element = logRef.current;
    if (!element) {
      return undefined;
    }
    const onScroll = () => {
      const fromBottom =
        element.scrollHeight - element.scrollTop - element.clientHeight;
      wasFollowing.current = fromBottom <= FOLLOW_SLACK_PX;
    };
    element.addEventListener("scroll", onScroll);
    return () => element.removeEventListener("scroll", onScroll);
  }, []);

  // Scrolled before the browser paints, so a new message never appears halfway
  // down and then jumps. Only when the reader was already at the bottom: yanking
  // someone back down while they are reading history is worse than missing a
  // line they can scroll to.
  useLayoutEffect(() => {
    const element = logRef.current;
    if (element && wasFollowing.current) {
      element.scrollTop = element.scrollHeight;
    }
  }, [messages]);

  const send = (event) => {
    event.preventDefault();
    const text = draft.trim();
    if (text === "") {
      return;
    }
    onSend(text);
    // Cleared on send rather than on the message coming back: the server may
    // drop it (flood check), and holding the box hostage until an echo arrives
    // would make a working chat feel broken.
    setDraft("");
  };

  return (
    <div style={panel}>
      <div style={log} ref={logRef} role="log" aria-label="Match chat">
        {messages.length === 0 ? (
          <div style={empty}>Nothing said yet.</div>
        ) : (
          messages.map((message) => (
            <div key={message.id}>
              <span style={stamp}>{timeOf(message.at)}</span>
              <strong
                style={{ color: SIDE_COLOURS[message.player_id] ?? NEUTRAL }}
              >
                {message.name}
                {message.player_id === mySide ? " (you)" : ""}
              </strong>
              {": "}
              {/* Rendered as text, never as markup: this came from another
                  player, and React escaping it is the whole defence. */}
              <span>{message.text}</span>
            </div>
          ))
        )}
      </div>
      <form style={form} onSubmit={send}>
        <input
          style={field}
          value={draft}
          // Matches the server's own bound, so the box stops where the message
          // would have been cut rather than silently losing the tail.
          maxLength={240}
          placeholder="Say something to the other side…"
          aria-label="Message"
          onChange={(event) => setDraft(event.target.value)}
          // Escape hands the keyboard back to the game, which is otherwise a
          // click on the canvas away.
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.currentTarget.blur();
            }
          }}
        />
        <button
          type="submit"
          style={sendButton(draft.trim() !== "")}
          disabled={draft.trim() === ""}
        >
          Send
        </button>
      </form>
    </div>
  );
};

export default ChatBox;
